/**
 * Single source for Stripe.js - ensures all checkout flows use the same publishable key.
 * Key must match the secret key used by: Edge Function (reserve) + Vercel (defera-zero).
 *
 * Stripe.js (about 270 KB) loads the first time a payment form asks for it, not when
 * the app starts: every page used to download it, the landing page included.
 * `stripePromise` is promise-like and calls loadStripe on its first `then` (an
 * <Elements> resolving it, or an `await`), so the payment components use it exactly as
 * before. The `/pure` entry is the one without the script tag the default entry adds
 * the moment it's imported.
 */
import { loadStripe } from '@stripe/stripe-js/pure';

// Strip any non-ASCII chars (e.g. Ø/%C3%98) that can corrupt the key and cause 401
const raw = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY?.trim() || '';
const key = raw.replace(/[^\x20-\x7E]/g, '');

// Validate: must be pk_live_ or pk_test_, ASCII only
const isValid = key && (key.startsWith('pk_live_') || key.startsWith('pk_test_')) && /^pk_[a-zA-Z0-9_]+$/.test(key);
if (!isValid && import.meta.env.DEV) {
  console.warn('[Stripe] Invalid or missing VITE_STRIPE_PUBLISHABLE_KEY. Expected pk_live_* or pk_test_*. Got:', key ? `${key.slice(0, 12)}...` : '(empty)');
}

let loading = null;
const load = () => (loading ||= loadStripe(key));

export const stripePromise = isValid
  ? {
    then: (onFulfilled, onRejected) => load().then(onFulfilled, onRejected),
    catch: (onRejected) => load().catch(onRejected),
    finally: (onFinally) => load().finally(onFinally),
  }
  : null;
